import { useState } from "react";
import { AnimatePresence, m } from "framer-motion";

import { billingApi } from "@/api/services";
import { useToast } from "@/components/ui/toast";
import { useModalA11y } from "@/hooks/useModalA11y";
import { useAuthStore, userHasRole } from "@/stores/auth";
import type { CreditBlockPayload } from "@/stores/creditBlock";
import { useCreditBlockStore } from "@/stores/creditBlock";

/** Credit packs offered when a user runs dry. */
const TOP_UP_AMOUNT = 50;

function CreditBlockBody({ block }: { block: CreditBlockPayload }) {
  const dismiss = useCreditBlockStore((s) => s.dismiss);
  const user = useAuthStore((s) => s.user);
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  // /billing/credits/checkout is ORG_ADMIN-gated. Rendering a "Buy credits"
  // button for anyone else would just 403 — a second dead end at the exact
  // moment we are asking them to spend money. Non-admins get told who can.
  const canBuy = userHasRole(user, "ORG_ADMIN");

  async function buyCredits() {
    setBusy(true);
    try {
      const here = window.location.href;
      const res = await billingApi.purchaseCredits(TOP_UP_AMOUNT, here, here);
      if (res.checkout_url) {
        window.location.assign(res.checkout_url);
        return;
      }
      // Manual/dev mode grants directly — no Stripe redirect.
      toast.success(`${res.credits_added ?? TOP_UP_AMOUNT} credits added.`);
      dismiss();
    } catch {
      toast.error("Couldn't start checkout. Please try again or contact support.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="p-6">
      <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
        You&apos;re out of AI credits
      </h2>
      <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{block.message}</p>

      <dl className="mt-4 grid grid-cols-2 gap-3 rounded-xl bg-slate-50 p-4 text-sm dark:bg-slate-800/60">
        <div>
          <dt className="text-slate-500 dark:text-slate-400">Remaining</dt>
          <dd className="mt-0.5 font-semibold tabular-nums text-slate-900 dark:text-slate-100">
            {block.credits_remaining}
          </dd>
        </div>
        <div>
          <dt className="text-slate-500 dark:text-slate-400">Needed</dt>
          <dd className="mt-0.5 font-semibold tabular-nums text-slate-900 dark:text-slate-100">
            {block.credits_required}
          </dd>
        </div>
      </dl>

      <div className="mt-6 flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={dismiss}
          className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          Not now
        </button>
        {canBuy ? (
          <button
            type="button"
            onClick={buyCredits}
            disabled={busy}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
          >
            {busy ? "Starting checkout…" : `Buy ${TOP_UP_AMOUNT} credits`}
          </button>
        ) : (
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Ask your organization admin to add credits.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * App-level modal shown whenever the AI credit quota blocks a request.
 *
 * Driven by useCreditBlockStore, which the HTTP client populates on a 402
 * carrying a `credit_quota_exceeded` body. Before this existed the payload fell
 * through to the generic error path and the user was shown a raw
 * `JSON.stringify(...)` blob at the most conversion-critical moment in the
 * product. Mounted once, at the App root, beside <ScopeChangeModal>.
 */
export function CreditBlockModal() {
  const block = useCreditBlockStore((s) => s.block);
  const seq = useCreditBlockStore((s) => s.seq);
  const dismiss = useCreditBlockStore((s) => s.dismiss);

  const panelRef = useModalA11y<HTMLDivElement>({ open: !!block, onClose: dismiss });

  return (
    <AnimatePresence>
      {block && (
        <m.div
          key="credit-block-backdrop"
          className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={dismiss}
        >
          <m.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Out of AI credits"
            tabIndex={-1}
            className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-slate-900"
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
          >
            <CreditBlockBody key={seq} block={block} />
          </m.div>
        </m.div>
      )}
    </AnimatePresence>
  );
}
